import { Component, OnInit, Input, Output, EventEmitter, ComponentFactoryResolver, ViewContainerRef } from '@angular/core';
import { DatePipe, DecimalPipe, CurrencyPipe, PercentPipe } from '@angular/common';
import { FormGroup, FormControl, NgForm } from '@angular/forms';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { GridButton, GridButtonEventData, GridColumn, GridColumnType, GridDefinition, GridEditMode, GridEditorType,
ColumnDataType, SortDirection, GridCellEventData } from './grid';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.css'],
  providers: [DatePipe, DecimalPipe, CurrencyPipe, PercentPipe]
})
export class GridComponent implements OnInit {

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef) { }

  private _definition: GridDefinition;
  private _data: any[];
  dataSet = false;
  definitionSet = false;

  @Input()
  public get data() : any[] {
    return this._data;
  }
  public set data(v : any[]) {
    this._data = v;
    this.onDataSet();
  }

  @Input()
  get definition(): GridDefinition {
    return this._definition;
  }
  set definition(value: GridDefinition) {
    this._definition = value;
    this.onDefinitionSet();
  }
  @Output()
  ButtonClicked = new EventEmitter();
  columnTypes = Object.assign({}, GridColumnType);
  dataTypes = Object.assign({}, ColumnDataType);
  fixedHeaderStyle: any;
  @Input()
  style = {};
  @Input()
  filter: string;
  @Output()
  SortChange = new EventEmitter();
  @Input()
  css = 'table';
  @Input()
  keyField = 'id';
  @Input()
  editMode = GridEditMode.NoEdit;
  @Input()
  editedRow: any;
  editModes = Object.assign({}, GridEditMode);
  editorTypes = Object.assign({}, GridEditorType);
  editValidation = false;
  expandStatuses = {} //key=c.name value = object with key=ix and value = boolean


  @Output()
  CellChanged = new EventEmitter<GridCellEventData>();


  ngOnInit() {
    this.fixedHeaderStyle = { height: this.calculateScrollableHeight(this.style['height']), 'overflow-y': 'auto'};
  }

  buttonClicked(d: any, c: GridColumn, button?: GridButton, form?: NgForm) {
    if (button != null) {
      if (!button.requiresValidation || form.valid) {
        this.ButtonClicked.emit(new GridButtonEventData(c.name, d, button.name));
      }
      if (button.requiresValidation && form.invalid) {
        this.editValidation = true;
      }
    } else {
      this.ButtonClicked.emit(new GridButtonEventData(c.name, d));
    }

  }  

  calculateScrollableHeight(styleHeight: string) {
    if ( styleHeight != null) {
      const num = +styleHeight.replace('px', '');
      if (num > 0) {
        return (num - 40).toString() + 'px';
      }
    }
    return '';
  }

  getValue(row: any, c: GridColumn, field?: string) {

    if (!field) {
        field = 'field';
    }
    if (c.fieldValueCallback == null) {
      let obj = row;
      const parts = c[field].split('.');
      for (let i = 0; i < parts.length - 1; i++) {
        if (obj[parts[i]] != null) {
          obj = obj[parts[i]];
        }
      }
      if (obj[parts[parts.length -1 ]] != null) {
        const retValue = obj[parts[parts.length - 1]];
        if(c.type != GridColumnType.Password) {
          return retValue;
        } else {
          return (c.passwordChar || '*').repeat(retValue.length);
        }
      } else {
        return '';
      }
    }
    return c.fieldValueCallback(row);
  }

  checkLength(value, c: GridColumn, ix: number) {
    if(!c.expandTextData) {
      return value;
    }
    if(!(ix in this.expandStatuses[c.name]) || !this.expandStatuses[c.name][ix]) {
      const sValue = value.toString();
      return sValue.length > c.expandTextData.maxLength ? sValue.substring(0,c.expandTextData.maxLength) : sValue;
    } else {
      return value;
    }
  }

  getExpandText(c: GridColumn, ix: number) {
    if(!c.expandTextData) {
      return null;
    }
    if(!(ix in this.expandStatuses[c.name]) || !this.expandStatuses[c.name][ix]) {
      return c.expandTextData.showAllText;
    } else {
      return c.expandTextData.showLessText;
    }
  }

  toggleExpand(c: GridColumn, ix: number) {
    const value = (!(ix in this.expandStatuses[c.name])) || !this.expandStatuses[c.name][ix];
    this.expandStatuses[c.name][ix] = value;
  }

  shouldShowExpand(row: any, c: GridColumn, ix: number) {
    const value = c.type != GridColumnType.List ? this.getValue(row, c) : this.joinListValue(row, c);
    return value?.length > c.expandTextData.maxLength;
    
  }

  checkSort(c: GridColumn) {
    return this.definition.sort != null && this.definition.sort.column.field === c.field;
  }

  toggleSort(c: GridColumn) {
    if (this.definition.sort == null) {
      this.definition.sort = {column : c, direction: SortDirection.Asc};
    } else {
      let direction = SortDirection.Asc;
      if (this.definition.sort.column.field === c.field && this.definition.sort.direction === SortDirection.Asc) {
        direction = SortDirection.Desc;
      }
      this.definition.sort.column = c;
      this.definition.sort.direction = direction;

    }
    this.SortChange.emit(this.definition.sort);
  }

  getSortIconClass(c: GridColumn) {
    const direction = this.definition.sort.direction === SortDirection.Asc ? 'up' : 'down';
    return `glyphicon glyphicon-arrow-${direction} fa fa-arrow-${direction} sort-icon`;
  }

  checkColumnVisibility(c: GridColumn) {
    if (this.definition.columnVisibilityCallback == null) {
      return true;
    }
    return this.definition.columnVisibilityCallback(c);
  }

  checkColumnButtonVisibility(row: any, c: GridColumn, button: string) {
    if (this.definition.columnButtonVisibilityCallback == null) {
      return true;
    }
    return this.definition.columnButtonVisibilityCallback(row, c.name, button);
  }

  checkColumnGroupDisabledStatus(row: any, c: GridColumn, button: string) {
    if (this.definition.columnButtonDisabledStatusCallback == null) {
      return false;
    }
    return this.definition.columnButtonDisabledStatusCallback(row, c.name, button);
  }

  checkColumnEditorVisibilityStatus(row: any, c: GridColumn) {
    if (this.definition.columnEditorVisibilityStatusCallback == null) {
      return true;
    }
    return this.definition.columnEditorVisibilityStatusCallback(row, c);
  }

  toggleValue(c: GridColumn) {
    this.data.forEach(d => d[c.field] = c.selected);
  }

  checkMode(c: GridColumn) {
    return c.editModes.find(m => m === this.editMode) != null;
  }

  onDefinitionSet() {
    this.definitionSet = true;
    if(this.dataSet) {
      //this.createFormControls();
    }
    for(const c of this.definition.columns) {
      if(c.expandTextData) {
        this.expandStatuses[c.name] = {};
      }
    }
  }

  onDataSet() {
    this.dataSet = true;
    if(this.definitionSet) {
      //this.createFormControls();
    }
  }

  onAutoCompleteSelected(c: GridColumn, $event: TypeaheadMatch ) {
    if (c.autoCompleteData.onSelectedCallback != null) {
      c.autoCompleteData.onSelectedCallback($event.item);
    }
  }

  onAutoCompleteLoading(c: GridColumn, $event: boolean) {
    if (c.autoCompleteData.loadingCallback != null) {
      c.autoCompleteData.loadingCallback($event);
    }
  }

  joinListValue(row: any, col: GridColumn) {
    const list = this.getValue(row, col);
    if (list.length) {
        return list.map(l => l[col.linkField]).join(', ');

    }
    return '';
  }

  isPassword(c: GridColumn) {
    return c.editorType == GridEditorType.Password || c.type == GridColumnType.Password;
  }

  async onPasswordCopy(value: string) {
    await navigator.clipboard.writeText(value);
  }

  /*createFormControls() {
    const controls: any = {};
    this._definition.columns.forEach(c => {
      if (c.editable) {
        controls[c.name] = new FormControl(c.name);
      }
      if (c.type == GridColumnType.Checkbox || c.type == GridColumnType.Textbox) {
        for (let i = 0; i < this._data.length; i++) {
          const name = `${i}_${c.name}`;
          controls[name] = new FormControl(c.name);
        }
      }
    });

    this.form = new FormGroup(controls);
  }*/

  onCellChange(row: any, c: GridColumn) {
    this.CellChanged.emit(new GridCellEventData('change', row, c));
  }

}






